/*
 * colorize-engine.js — shared, framework-agnostic AI colorization engine.
 *
 * Pure inference + CIE-Lab composite + post-process logic, extracted so both
 * /video-colorize (per-frame) and /photo-colorize (single image) can share it.
 * No DOM/tool state coupling: you pass canvases + sessions + plain option
 * objects. Two engines: DDColor (photo, ab-out) and manga-colorization-v2
 * (cartoon, RGB-out → converted to ab). Both recombine with the SOURCE
 * luminance at composite time, so all original detail is preserved.
 *
 * Models are loaded by the consumer via @heavy()-routed URLs (DDColor int8/fp16,
 * manga fp16) — already hosted on HF + the RU edge. ORT (onnxruntime-web) is
 * lazy-loaded from a configurable primary/fallback pair.
 */
(function (root) {
    'use strict';

    var DD_SIZE = 256;       // DDColor input + the ab plane size both engines composite from
    var MANGA_SIZE = 384;    // manga model run size (%32); RGB out → downscaled to ab@256
    var _ort = { primary: '/vendor/onnxruntime/ort.min.js', fallback: 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.24.3/dist/ort.min.js', wasmBase: '/vendor/onnxruntime/' };

    function configure(cfg) { if (cfg && cfg.ort) { for (var k in cfg.ort) _ort[k] = cfg.ort[k]; } }

    // ─── Colour-space (sRGB ↔ linear ↔ CIE Lab) ──────────────────────────
    function srgbToLinear(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    function linearToSrgb(c) { var v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; return Math.round(Math.max(0, Math.min(1, v)) * 255); }

    function rgbToLabAb(r, g, b) {
        var lr = srgbToLinear(r), lg = srgbToLinear(g), lb = srgbToLinear(b);
        var x = (lr * 0.4124 + lg * 0.3576 + lb * 0.1805) / 0.95047;
        var y = lr * 0.2126 + lg * 0.7152 + lb * 0.0722;
        var z = (lr * 0.0193 + lg * 0.1192 + lb * 0.9505) / 1.08883;
        function f(t) { return t > 0.008856 ? Math.cbrt(t) : (7.787 * t + 16 / 116); }
        var fx = f(x), fy = f(y), fz = f(z);
        return [500 * (fx - fy), 200 * (fy - fz)];
    }

    // Recombine an a*b* plane with the source image's luminance → RGB.
    function compositeLab(srcImg, abPlane, outImg) {
        var sd = srcImg.data, od = outImg.data, n = sd.length;
        var w = srcImg.width, h = srcImg.height;
        function fInv(t) { var t3 = t * t * t; return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787; }
        for (var i = 0, j = 0; i < n; i += 4, j++) {
            var lr = srgbToLinear(sd[i]), lg = srgbToLinear(sd[i + 1]), lb = srgbToLinear(sd[i + 2]);
            var y = lr * 0.2126 + lg * 0.7152 + lb * 0.0722;
            var fy = y > 0.008856 ? Math.cbrt(y) : (7.787 * y + 16 / 116);
            var L = 116 * fy - 16;
            var a = abPlane[j], b = abPlane[w * h + j];
            var fy2 = (L + 16) / 116, fx = a / 500 + fy2, fz = fy2 - b / 200;
            var X = 0.95047 * fInv(fx), Y = fInv(fy2), Z = 1.08883 * fInv(fz);
            var r = X * 3.2406 + Y * -1.5372 + Z * -0.4986;
            var g = X * -0.9689 + Y * 1.8758 + Z * 0.0415;
            var b2 = X * 0.0557 + Y * -0.2040 + Z * 1.0570;
            od[i] = linearToSrgb(r); od[i + 1] = linearToSrgb(g); od[i + 2] = linearToSrgb(b2); od[i + 3] = 255;
        }
    }

    // Bilinear upsample of a 2-channel ab plane (256² → W×H).
    function upsampleAb(abSmall, srcSize, dstW, dstH) {
        var dst = new Float32Array(dstW * dstH * 2);
        var sx = (srcSize - 1) / Math.max(1, dstW - 1), sy = (srcSize - 1) / Math.max(1, dstH - 1);
        for (var c = 0; c < 2; c++) {
            var srcOff = c * srcSize * srcSize, dstOff = c * dstW * dstH;
            for (var y = 0; y < dstH; y++) {
                var fy = y * sy, y0 = Math.floor(fy), y1 = Math.min(srcSize - 1, y0 + 1), wy = fy - y0;
                for (var x = 0; x < dstW; x++) {
                    var fx = x * sx, x0 = Math.floor(fx), x1 = Math.min(srcSize - 1, x0 + 1), wx = fx - x0;
                    var a = abSmall[srcOff + y0 * srcSize + x0] * (1 - wx) + abSmall[srcOff + y0 * srcSize + x1] * wx;
                    var bb = abSmall[srcOff + y1 * srcSize + x0] * (1 - wx) + abSmall[srcOff + y1 * srcSize + x1] * wx;
                    dst[dstOff + y * dstW + x] = a * (1 - wy) + bb * wy;
                }
            }
        }
        return dst;
    }

    // Single-image composite from a cached 256² ab plane → full-res ImageData.
    function compositeFromAb(srcCanvas, abSmall) {
        var w = srcCanvas.width, h = srcCanvas.height;
        var ctx = srcCanvas.getContext('2d', { willReadFrequently: true });
        var srcImg = ctx.getImageData(0, 0, w, h);
        var abFull = upsampleAb(abSmall, DD_SIZE, w, h);
        var outImg = ctx.createImageData(w, h);
        compositeLab(srcImg, abFull, outImg);
        return outImg;
    }

    // ─── Hue correction (fix a wrong hue range) ──────────────────────────
    function _rgb2hsl(r, g, b) {
        r /= 255; g /= 255; b /= 255;
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b), h = 0, s = 0, l = (mx + mn) / 2;
        if (mx !== mn) {
            var dl = mx - mn;
            s = l > 0.5 ? dl / (2 - mx - mn) : dl / (mx + mn);
            if (mx === r) h = (g - b) / dl + (g < b ? 6 : 0);
            else if (mx === g) h = (b - r) / dl + 2;
            else h = (r - g) / dl + 4;
            h /= 6;
        }
        return [h, s, l];
    }
    function _hue2rgb(p, q, t) { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; }
    function _hsl2rgb(h, s, l) {
        if (s === 0) { var v = Math.round(l * 255); return [v, v, v]; }
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        return [Math.round(_hue2rgb(p, q, h + 1 / 3) * 255), Math.round(_hue2rgb(p, q, h) * 255), Math.round(_hue2rgb(p, q, h - 1 / 3) * 255)];
    }
    function _hueDist(a, b) { var d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; }
    function _lerpHue(a, b, t) { var d = ((b - a + 540) % 360) - 180; return (a + d * t + 360) % 360; }
    // hue = { on, from(deg), tol(deg), mode:'desat'|'recolor', to(deg), strength(0..100) }
    function applyHueRemap(outImg, hue) {
        if (!hue || !hue.on) return;
        var d = outImg.data, n = d.length, from = hue.from, tol = Math.max(1, hue.tol);
        var strength = hue.strength / 100, recolor = hue.mode === 'recolor', to = hue.to;
        for (var i = 0; i < n; i += 4) {
            var hsl = _rgb2hsl(d[i], d[i + 1], d[i + 2]);
            if (hsl[1] < 0.04) continue;
            var hd = _hueDist(hsl[0] * 360, from);
            if (hd >= tol) continue;
            var w = strength * (1 - hd / tol);
            var rgb = recolor ? _hsl2rgb(_lerpHue(hsl[0] * 360, to, w) / 360, hsl[1], hsl[2]) : _hsl2rgb(hsl[0], hsl[1] * (1 - w), hsl[2]);
            d[i] = rgb[0]; d[i + 1] = rgb[1]; d[i + 2] = rgb[2];
        }
    }

    // Brightness/contrast metric to normalize under/over-exposed input before
    // DDColor (it was trained on roughly-normalized luminance).
    function computeNormMetric(srcCanvas) {
        try {
            var w = srcCanvas.width, h = srcCanvas.height;
            if (!w || !h) return { brightness: 1, contrast: 1 };
            var c = document.createElement('canvas'); c.width = 64; c.height = 64;
            var cx = c.getContext('2d'); cx.drawImage(srcCanvas, 0, 0, 64, 64);
            var d = cx.getImageData(0, 0, 64, 64).data;
            var n = 64 * 64, sum = 0, sumSq = 0;
            for (var i = 0; i < d.length; i += 4) { var l = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255; sum += l; sumSq += l * l; }
            var mean = sum / n, vari = (sumSq / n) - mean * mean, std = Math.sqrt(Math.max(0.0001, vari));
            return { brightness: Math.max(0.7, Math.min(1.4, 0.5 / Math.max(0.15, mean))), contrast: Math.max(0.7, Math.min(1.4, 0.20 / Math.max(0.05, std))) };
        } catch (_) { return { brightness: 1, contrast: 1 }; }
    }

    // ─── ORT loading + model fetch + sessions ────────────────────────────
    var ortLoadingPromise = null;
    function loadOrt() {
        if (root.ort) return Promise.resolve();
        if (ortLoadingPromise) return ortLoadingPromise;
        ortLoadingPromise = new Promise(function (resolve, reject) {
            function loadFrom(url, onFail) {
                var sc = document.createElement('script');
                sc.src = url;
                sc.onload = function () {
                    try {
                        if (root.ort && ort.env && ort.env.wasm) {
                            // Absolute URL for ORT-Web internal `import()` of
                            // jsep helpers (apollo-worker.js:78-91 reasoning).
                            var _wp = _ort.wasmBase;
                            if (_wp.charAt(0) === '/' && typeof location !== 'undefined' && location.origin) _wp = location.origin + _wp;
                            ort.env.wasm.wasmPaths = _wp;
                            ort.env.wasm.numThreads = (!_ort.legacy && typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated) ? Math.min(navigator.hardwareConcurrency || 4, 4) : 1;   // the legacy runtime has no threaded wasm
                        }
                    } catch (_) {}
                    resolve();
                };
                sc.onerror = onFail;
                document.head.appendChild(sc);
            }
            loadFrom(_ort.primary, function () { loadFrom(_ort.fallback, function () { ortLoadingPromise = null; reject(new Error('Failed to load ORT')); }); });
        });
        return ortLoadingPromise;
    }

    /* Модель качает ОБЩИЙ загрузчик (public/js/model-download.js →
       self.__mdFetchBytes): докачка по Range после обрыва, сторож молчания,
       рвущий сам fetch, сверка полноты, чистка огрызка из кэшей и запись в
       кэш моделей (первый визит идёт мимо Service Worker — без этой записи
       следующий визит качал бы модель заново).

       До 09.10.2026 здесь был свой однопроходный fetch — и оба колоризатора
       жили без всего этого. Обращение «НЕ РАСКРАШИВАЕТ» (timbrica.com/ru,
       медленный канал): 59 МБ качались 7,5 мин и оборвались «network
       error», вторая попытка С НУЛЯ — ещё 7,8 мин и тот же обрыв; за три
       недели так же ещё минимум три сессии. На канале, где соединение не
       живёт дольше одной загрузки, загрузка с нуля не завершается никогда,
       а докачка — завершается. Хуже того, поток короче заголовка (прокси
       закрыл соединение чисто) уходил в ORT как модель — «protobuf parsing
       failed» — и оседал в кэше моделей: тул ломался у человека навсегда.
       Стенд — tests/playwright/photo-colorize-model-survives-break.mjs.

       Запасной путь (примитив не загрузился) хотя бы сверяет полноту и в
       кэш ничего не кладёт: честно упасть лучше, чем скормить огрызок. */
    async function fetchModelOnce(url, onProgress, signal) {
        var md = (typeof self !== 'undefined' && self.__mdFetchBytes) || null;
        if (md) {
            return md(url, {
                signal: signal,
                onProgress: function (loaded, total) { if (onProgress && total) onProgress(loaded / total, loaded, total); }
            });
        }
        var resp = await fetch(url, signal ? { signal: signal } : undefined);
        if (!resp.ok) throw new Error('Model unavailable (HTTP ' + resp.status + ')');
        var total = parseInt(resp.headers.get('Content-Length'), 10) || 0;
        var reader = resp.body.getReader(), chunks = [], loaded = 0;
        while (true) { var r = await reader.read(); if (r.done) break; chunks.push(r.value); loaded += r.value.length; if (onProgress && total) onProgress(loaded / total, loaded, total); }
        if (total > 0 && loaded < total) throw new Error('Model truncated: ' + loaded + '/' + total + ' from ' + url);
        var data = new Uint8Array(loaded), off = 0;
        for (var i = 0; i < chunks.length; i++) { data.set(chunks[i], off); off += chunks[i].length; }
        return data;
    }
    /* Ошибка ЗАГРУЗКИ помечена кодом MODEL_DOWNLOAD: модель не доехала по
       сети — это не сбой воркера и не нехватка памяти. Вызывающий по этой
       метке не уходит в «запасной путь в главном потоке» (он качал бы те же
       адреса с нуля) и говорит человеку про связь, а не «не удалось
       раскрасить». */
    function _downloadError(cause, url) {
        var e = new Error((cause && cause.message) || 'Model unavailable');
        e.code = 'MODEL_DOWNLOAD';
        e.cause = cause;
        e.url = url || '';
        return e;
    }
    function isDownloadError(e) { return !!(e && e.code === 'MODEL_DOWNLOAD'); }
    // Walk [primary, fallback] so a 404/CORS-fail on one path falls through.
    // A user abort (signal) is NOT a fall-through — rethrow immediately.
    // opts.onFallback(index, err) — the primary gave up, the next URL starts.
    async function fetchModel(urls, onProgress, signal, opts) {
        var list = Array.isArray(urls) ? urls.filter(Boolean) : [urls], lastErr, lastUrl;
        for (var u = 0; u < list.length; u++) {
            try { return await fetchModelOnce(list[u], onProgress, signal); }
            catch (e) {
                if (signal && signal.aborted) throw e;
                lastErr = e; lastUrl = list[u];
                if (u + 1 < list.length && opts && opts.onFallback) { try { opts.onFallback(u, e); } catch (_) {} }
            }
        }
        throw _downloadError(lastErr, lastUrl);
    }

    async function hasWebGPU() {
        if (!('gpu' in navigator)) return false;
        try { return !!(await navigator.gpu.requestAdapter()); } catch (_) { return false; }
    }
    function _abErr() { try { return new DOMException('aborted', 'AbortError'); } catch (_) { var e = new Error('aborted'); e.name = 'AbortError'; return e; } }

    /* «no available backend found. ERR: [wasm] RangeError: Out of memory» at the
       first session is not this device running out of memory: the browser could
       not RESERVE the shared 4 GiB the current ORT build imports (WebKit before
       18). The owner (window.__ortBuild) marks the browser and answers ORT 1.18,
       which owns an ordinary growing memory; every model both colorizers load
       creates on it. true = the engine now points at 1.18, the runtime that
       failed is dropped (a failed init is cached by it) — create once more. */
    function _noShareStepDown(e, signal) {
        if (signal && signal.aborted) return false;
        var B = root.__ortBuild;
        if (!(B && B.initFailed && B.initFailed(e, { legacy: _ort.legacy ? 'legacy' : '', from: 'colorize' }))) return false;
        var L = B.legacy && B.legacy();
        if (!L) return false;
        _ort.primary = L.url; _ort.fallback = L.fallbackUrl; _ort.wasmBase = L.wasmBase; _ort.legacy = true;
        resetWorker();
        try { delete root.ort; } catch (_) { try { root.ort = undefined; } catch (__) {} }
        ortLoadingPromise = null;
        return true;
    }
    function _pause(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }   // an immediate retry meets the same address space
    async function createSession(urls, opts) {
        try { return await _createSession(urls, opts); }
        catch (e) {
            if (!_noShareStepDown(e, opts && opts.signal)) throw e;
            await _pause(1000);
            return _createSession(urls, opts);
        }
    }

    // Create an ONNX session. opts = { onProgress, onStatus, preferWebGPU, signal }.
    // Returns { session, provider, sizeMB }. The 62-113 MB session compile blocks the
    // main thread; we surface a status + yield a frame first so the UI repaints (shows
    // "colorizing", not a frozen "model 100%") before the block.
    async function _createSession(urls, opts) {
        opts = opts || {};
        await loadOrt();
        var data = await fetchModel(urls, opts.onProgress, opts.signal);
        if (opts.signal && opts.signal.aborted) throw _abErr();
        if (opts.onStatus) opts.onStatus('compiling');
        await new Promise(function (r) { setTimeout(r, 16); });   // let the overlay repaint before the blocking compile
        if (opts.signal && opts.signal.aborted) throw _abErr();
        var webgpu = opts.preferWebGPU ? await hasWebGPU() : false;
        var providers = webgpu ? ['webgpu', 'wasm'] : ['wasm'];
        // 'basic', NOT 'all'. ORT's full optimizer corrupts the fp16 DDColor
        // graph and create() throws «…InsertedPrecisionFreeCast_… does not
        // exist» on the wasm-only path. Production proof: over 90 days the fp16
        // tier loaded on WebGPU 95 times and on wasm ZERO times — it could not.
        // 'basic' loads there and is 36% faster on WebGPU too. Measured
        // 2026-08-13, tests/playwright/ddcolor-model-dominance.mjs.
        var session = await ort.InferenceSession.create(data.buffer, { executionProviders: providers, graphOptimizationLevel: opts.graphOpt || 'basic' });
        return { session: session, provider: providers[0], sizeMB: Math.round(data.length / 1048576) };
    }

    // ─── Inference → 256² ab plane ───────────────────────────────────────
    // Build DDColor's 3-channel input from RGBA pixels, GRAYSCALE-NORMALIZED
    // (luma replicated to R=G=B). DDColor is a grayscale-colorization network;
    // feeding a tinted source (sepia / aged photo) is out-of-distribution — it
    // loses chroma (measured on the int8 model: sepia raw mean-ab 9.15 vs gray
    // 10.5) AND leaves the composite carrying a residual cast, so an old sepia
    // photo came out LESS colourful than the input (bug 2026-07-06,
    // jayferpeakee@yahoo.com: "coloured nothing, just took out the sepia"). A
    // clean B&W source is already R=G=B, so this is a no-op there.
    function _grayInput(d, n) {
        var inp = new Float32Array(3 * n);
        for (var i = 0, j = 0; j < n; i += 4, j++) {
            var lum = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
            inp[j] = lum; inp[n + j] = lum; inp[2 * n + j] = lum;
        }
        return inp;
    }

    // DDColor (photo). opts.autoNorm = {brightness,contrast} or null.
    async function inferDDColor(session, srcCanvas, opts) {
        opts = opts || {};
        var s = DD_SIZE, c = document.createElement('canvas'); c.width = s; c.height = s;
        var ctx = c.getContext('2d');
        if (opts.autoNorm) ctx.filter = 'brightness(' + opts.autoNorm.brightness.toFixed(3) + ') contrast(' + opts.autoNorm.contrast.toFixed(3) + ')';
        ctx.drawImage(srcCanvas, 0, 0, s, s);
        ctx.filter = 'none';
        var d = ctx.getImageData(0, 0, s, s).data, n = s * s, inp = _grayInput(d, n);
        var feeds = {}; feeds[session.inputNames[0]] = new ort.Tensor('float32', inp, [1, 3, s, s]);
        var out = await session.run(feeds);
        return out[session.outputNames[0]].data;
    }

    // manga-colorization-v2 (cartoon). Throws on run failure (caller falls back).
    async function inferManga(session, srcCanvas) {
        var ms = MANGA_SIZE, n = ms * ms;
        var mc = document.createElement('canvas'); mc.width = ms; mc.height = ms;
        var mx = mc.getContext('2d', { willReadFrequently: true });
        mx.drawImage(srcCanvas, 0, 0, ms, ms);
        var md = mx.getImageData(0, 0, ms, ms).data, inp = new Float32Array(5 * n);   // ch1-4 = 0 (no hint)
        for (var i = 0, j = 0; j < n; i += 4, j++) inp[j] = (0.2126 * md[i] + 0.7152 * md[i + 1] + 0.0722 * md[i + 2]) / 255;
        var feeds = {}; feeds[session.inputNames[0]] = new ort.Tensor('float32', inp, [1, 5, ms, ms]);
        var out = await session.run(feeds);
        var rgb = out[session.outputNames[0]].data;                                    // [1,3,ms,ms] 0..1 planar
        var fc = document.createElement('canvas'); fc.width = ms; fc.height = ms;
        var fcx = fc.getContext('2d'), fimg = fcx.createImageData(ms, ms), fd = fimg.data;
        for (var p = 0, q = 0; p < n; p++, q += 4) {
            fd[q] = Math.max(0, Math.min(255, (rgb[p] * 255) | 0));
            fd[q + 1] = Math.max(0, Math.min(255, (rgb[n + p] * 255) | 0));
            fd[q + 2] = Math.max(0, Math.min(255, (rgb[2 * n + p] * 255) | 0));
            fd[q + 3] = 255;
        }
        fcx.putImageData(fimg, 0, 0);
        var s = DD_SIZE, sn = s * s, sc = document.createElement('canvas'); sc.width = s; sc.height = s;
        var scx = sc.getContext('2d', { willReadFrequently: true });
        scx.drawImage(fc, 0, 0, s, s);
        var sd = scx.getImageData(0, 0, s, s).data, ab = new Float32Array(2 * sn);
        for (var k = 0, mm = 0; mm < sn; k += 4, mm++) { var lab = rgbToLabAb(sd[k], sd[k + 1], sd[k + 2]); ab[mm] = lab[0]; ab[sn + mm] = lab[1]; }
        return ab;
    }

    // Mock ab so the pipeline is testable without a model. Synthesizes a plausible
    // cinematic ab from luminance alone (warm shadows, cool highlights) so __PC_MOCK
    // demonstrates colorization even on a pure grayscale source — where a hue-rotate
    // filter is a no-op. Not used in production; the real models invent colour from
    // luminance the same way.
    function mockAb(srcCanvas) {
        var s = DD_SIZE, c = document.createElement('canvas'); c.width = s; c.height = s;
        var cx = c.getContext('2d'); cx.drawImage(srcCanvas, 0, 0, s, s);
        var d = cx.getImageData(0, 0, s, s).data, n = s * s, ab = new Float32Array(2 * n);
        for (var i = 0, j = 0; i < d.length; i += 4, j++) {
            var t = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
            ab[j] = 18 + 22 * (0.5 - t);     // a*: warm red, stronger in shadows
            ab[n + j] = 28 * (0.55 - t);     // b*: yellow shadows → blue highlights
        }
        return ab;
    }

    // Global saturation / vibrance — scale each pixel's chroma around its own
    // luminance. amt in [-100, +100]; 0 = unchanged, -100 = grayscale, +100 = 2×.
    // DDColor can come out muted or over-eager; this is the single master tweak.
    function applySaturation(outImg, amt) {
        var f = 1 + amt / 100, d = outImg.data;
        for (var i = 0; i < d.length; i += 4) {
            var lum = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
            d[i] = _c8(lum + (d[i] - lum) * f);
            d[i + 1] = _c8(lum + (d[i + 1] - lum) * f);
            d[i + 2] = _c8(lum + (d[i + 2] - lum) * f);
        }
    }
    function _c8(v) { return v < 0 ? 0 : (v > 255 ? 255 : v | 0); }

    // Vibrance — like saturation but the boost scales DOWN as a pixel's existing
    // saturation rises, so muted areas (which DDColor int8 leaves undersaturated)
    // get lifted to a natural, colourful look while already-vivid pixels (skin,
    // sky) are barely touched and don't clip. amt in [0, 100]. This is the
    // baseline that makes the muted model output read as real colour — without
    // it an aged/sepia source (input saturation ~30%) beats our ~18-21% output
    // and the user perceives "it removed the tint but coloured nothing".
    function applyVibrance(outImg, amt) {
        if (!amt) return;
        var a = amt / 100, d = outImg.data;
        for (var i = 0; i < d.length; i += 4) {
            var r = d[i], g = d[i + 1], b = d[i + 2];
            var mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
            if (mx <= 0) continue;
            var mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
            var sat = (mx - mn) / mx;                 // 0..1 current saturation
            var f = 1 + a * (1 - sat);                // low-sat → full boost, high-sat → ~none
            var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            d[i] = _c8(lum + (r - lum) * f); d[i + 1] = _c8(lum + (g - lum) * f); d[i + 2] = _c8(lum + (b - lum) * f);
        }
    }

    // ─── Post-process look (reuses the global VideoColorize* helper modules) ──
    // look = { preset, presetMix, skin, reference, saturation, grain, grainIntensity, hue }
    function applyLook(outImg, look) {
        look = look || {};
        // Baseline vibrance FIRST — lift the muted model output to a natural,
        // colourful look before any preset/reference/user-saturation delta sits
        // on top. Photo engine only (manga/cartoon RGB is already saturated).
        if (look.baseVibrance) applyVibrance(outImg, look.baseVibrance);
        if (look.preset && look.preset !== 'modern' && look.presetMix > 0 && root.VideoColorizePresets) {
            VideoColorizePresets.applyToImageData(outImg, look.preset, look.presetMix);
        }
        if (look.skin && root.VideoColorizePresets) {
            var c = document.createElement('canvas'); c.width = outImg.width; c.height = outImg.height;
            var cx = c.getContext('2d'); cx.putImageData(outImg, 0, 0);
            VideoColorizePresets.applySkinTone(cx, look.skin);
            outImg.data.set(cx.getImageData(0, 0, c.width, c.height).data);
        }
        if (look.reference && root.VideoColorizePalette) VideoColorizePalette.applyToImageData(outImg, look.reference);
        if (look.saturation != null && look.saturation !== 0) applySaturation(outImg, look.saturation);
        applyHueRemap(outImg, look.hue);
        if (look.grain && look.grainIntensity > 0 && root.VideoColorizeGrain) VideoColorizeGrain.applyToImageData(outImg, look.grainIntensity);
    }

    // ─── Web Worker (off-main-thread ORT compile + inference) ────────────────
    // Model bytes are fetched here (progress + cancel) and transferred in; input
    // pixels are prepared here (canvas) and transferred in; the worker compiles +
    // runs (the parts that blocked the main thread) and transfers the tensor back.
    var _worker = null, _workerReady = null, _wId = 0, _wPending = {}, _workerKeys = {};
    function hasWorker() { return typeof Worker !== 'undefined'; }
    function _ensureWorker(workerUrl) {
        if (_workerReady) return _workerReady;
        _workerReady = new Promise(function (resolve, reject) {
            var w;
            try { w = new Worker(workerUrl); } catch (e) { _workerReady = null; reject(e); return; }
            w.onmessage = function (ev) {
                var m = ev.data || {};
                if (m.type === 'init-ok') { resolve(); return; }
                if (m.type === 'init-error') { _worker = null; _workerReady = null; reject(new Error(m.message)); return; }
                var p = _wPending[m.id]; if (!p) return; delete _wPending[m.id];
                if (m.type === 'error') p.reject(new Error(m.message)); else p.resolve(m);
            };
            w.onerror = function () { var pend = _wPending; _wPending = {}; for (var k in pend) { try { pend[k].reject(new Error('worker crashed')); } catch (_) {} } _worker = null; _workerReady = null; reject(new Error('worker crashed')); };
            _worker = w;
            w.postMessage({ cmd: 'init', ortPrimary: _ort.primary, ortFallback: _ort.fallback, wasmBase: _ort.wasmBase, legacy: !!_ort.legacy });
        });
        return _workerReady;
    }
    function _wCall(msg, transfer) {
        return new Promise(function (res, rej) { var id = ++_wId; msg.id = id; _wPending[id] = { resolve: res, reject: rej }; _worker.postMessage(msg, transfer || []); });
    }
    function resetWorker() { try { if (_worker) _worker.terminate(); } catch (_) {} _worker = null; _workerReady = null; _wPending = {}; _workerKeys = {}; }

    async function createWorkerSession(workerUrl, key, urls, opts) {
        try { return await _createWorkerSession(workerUrl, key, urls, opts); }
        catch (e) {
            // the model went to the worker that failed; the second fetch reads the cache
            if (!_noShareStepDown(e, opts && opts.signal)) throw e;
            await _pause(1000);
            return _createWorkerSession(workerUrl, key, urls, opts);
        }
    }
    async function _createWorkerSession(workerUrl, key, urls, opts) {
        opts = opts || {};
        await _ensureWorker(workerUrl);
        if (_workerKeys[key]) return _workerKeys[key];          // already compiled in the worker
        var data = await fetchModel(urls, opts.onProgress, opts.signal);
        if (opts.signal && opts.signal.aborted) throw _abErr();
        if (opts.onStatus) opts.onStatus('compiling');
        await new Promise(function (r) { setTimeout(r, 16); }); // repaint before the (now worker-side) compile
        if (opts.signal && opts.signal.aborted) throw _abErr();
        var buf = data.buffer, sz = data.length;
        var res = await _wCall({ cmd: 'session', key: key, modelBytes: buf, preferWebGPU: !!opts.preferWebGPU, graphOpt: opts.graphOpt || 'basic' }, [buf]);
        var handle = { worker: true, key: key, inputName: res.inputName, outputName: res.outputName, provider: res.provider, sizeMB: Math.round(sz / 1048576) };
        _workerKeys[key] = handle;
        return handle;
    }
    async function inferDDColorWorker(handle, srcCanvas, opts) {
        opts = opts || {};
        var s = DD_SIZE, c = document.createElement('canvas'); c.width = s; c.height = s;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        if (opts.autoNorm) ctx.filter = 'brightness(' + opts.autoNorm.brightness.toFixed(3) + ') contrast(' + opts.autoNorm.contrast.toFixed(3) + ')';
        ctx.drawImage(srcCanvas, 0, 0, s, s); ctx.filter = 'none';
        var d = ctx.getImageData(0, 0, s, s).data, n = s * s, inp = _grayInput(d, n);   // grayscale-normalize (see inferDDColor)
        var res = await _wCall({ cmd: 'run', key: handle.key, inputName: handle.inputName, outputName: handle.outputName, input: inp, dims: [1, 3, s, s] }, [inp.buffer]);
        return res.output;                                      // ab [1,2,256,256]
    }
    async function inferMangaWorker(handle, srcCanvas) {
        var ms = MANGA_SIZE, n = ms * ms;
        var mc = document.createElement('canvas'); mc.width = ms; mc.height = ms;
        var mx = mc.getContext('2d', { willReadFrequently: true });
        mx.drawImage(srcCanvas, 0, 0, ms, ms);
        var md = mx.getImageData(0, 0, ms, ms).data, inp = new Float32Array(5 * n);
        for (var i = 0, j = 0; j < n; i += 4, j++) inp[j] = (0.2126 * md[i] + 0.7152 * md[i + 1] + 0.0722 * md[i + 2]) / 255;
        var res = await _wCall({ cmd: 'run', key: handle.key, inputName: handle.inputName, outputName: handle.outputName, input: inp, dims: [1, 5, ms, ms] }, [inp.buffer]);
        var rgb = res.output;                                   // [1,3,ms,ms] 0..1 planar → ab (main-thread tail)
        var fc = document.createElement('canvas'); fc.width = ms; fc.height = ms;
        var fcx = fc.getContext('2d', { willReadFrequently: true }), fimg = fcx.createImageData(ms, ms), fd = fimg.data;
        for (var p = 0, q = 0; p < n; p++, q += 4) { fd[q] = _c8(rgb[p] * 255); fd[q + 1] = _c8(rgb[n + p] * 255); fd[q + 2] = _c8(rgb[2 * n + p] * 255); fd[q + 3] = 255; }
        fcx.putImageData(fimg, 0, 0);
        var sds = DD_SIZE, sn = sds * sds, sc = document.createElement('canvas'); sc.width = sds; sc.height = sds;
        var scx = sc.getContext('2d', { willReadFrequently: true }); scx.drawImage(fc, 0, 0, sds, sds);
        var sd = scx.getImageData(0, 0, sds, sds).data, ab = new Float32Array(2 * sn);
        for (var k = 0, mm = 0; mm < sn; k += 4, mm++) { var lab = rgbToLabAb(sd[k], sd[k + 1], sd[k + 2]); ab[mm] = lab[0]; ab[sn + mm] = lab[1]; }
        return ab;
    }

    // ── User-guided hints (siggraph17 net): tap a spot, the net recolours the
    // whole region edge-aware. Inputs: raw L (0..100), sparse ab hints (raw Lab)
    // + 0/1 mask; the net normalizes internally. hints = [{x,y,a,b,rad}] in
    // DD_SIZE (256²) space.
    function _hintPlanes(srcCanvas, hints) {
        var s = DD_SIZE, n = s * s;
        var c = document.createElement('canvas'); c.width = s; c.height = s;
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.drawImage(srcCanvas, 0, 0, s, s);
        var d = cx.getImageData(0, 0, s, s).data;
        var l = new Float32Array(n), h = new Float32Array(2 * n), m = new Float32Array(n);
        for (var i = 0, j = 0; j < n; i += 4, j++) {
            var y = srgbToLinear(d[i]) * 0.2126 + srgbToLinear(d[i + 1]) * 0.7152 + srgbToLinear(d[i + 2]) * 0.0722;
            var fy = y > 0.008856 ? Math.cbrt(y) : (7.787 * y + 16 / 116);
            l[j] = 116 * fy - 16;
        }
        (hints || []).forEach(function (p) {
            var rad = p.rad == null ? 2 : p.rad;
            for (var yy = p.y - rad; yy <= p.y + rad; yy++) for (var xx = p.x - rad; xx <= p.x + rad; xx++) {
                if (xx < 0 || yy < 0 || xx >= s || yy >= s) continue;
                var k = yy * s + xx; h[k] = p.a; h[n + k] = p.b; m[k] = 1;
            }
        });
        return { l: l, hints: h, mask: m };
    }
    async function inferHints(session, srcCanvas, hints) {
        var s = DD_SIZE, p = _hintPlanes(srcCanvas, hints), feeds = {};
        feeds.l = new ort.Tensor('float32', p.l, [1, 1, s, s]);
        feeds.hints = new ort.Tensor('float32', p.hints, [1, 2, s, s]);
        feeds.mask = new ort.Tensor('float32', p.mask, [1, 1, s, s]);
        var out = await session.run(feeds);
        return out[session.outputNames[0]].data;   // ab 256² raw Lab
    }
    async function inferHintsWorker(handle, srcCanvas, hints) {
        var s = DD_SIZE, p = _hintPlanes(srcCanvas, hints);
        var res = await _wCall({ cmd: 'run', key: handle.key, outputName: handle.outputName, feeds: [
            { name: 'l', data: p.l, dims: [1, 1, s, s] },
            { name: 'hints', data: p.hints, dims: [1, 2, s, s] },
            { name: 'mask', data: p.mask, dims: [1, 1, s, s] }
        ] }, [p.l.buffer, p.hints.buffer, p.mask.buffer]);
        return res.output;
    }
    // Blend the hinted pass into a base ab plane only where the hint net moved
    // relative to its own no-hint pass (|Δab| smoothstepped through t0..t1).
    // Outside the region w=0 ⇒ the base (e.g. DDColor's result) stays untouched
    // pixel-for-pixel; inside w→1 ⇒ the user's propagated colour. Measured on
    // real photos: beats both a pure hint pass (blotchy on complex scenes) and
    // a DDColor anchor grid (chokes large regions like skies).
    function regionBlend(baseAb, autoAb, hintedAb, t0, t1) {
        var n = DD_SIZE * DD_SIZE, out = new Float32Array(2 * n);
        t0 = t0 == null ? 6 : t0; t1 = t1 == null ? 18 : t1;
        for (var j = 0; j < n; j++) {
            var da = hintedAb[j] - autoAb[j], db = hintedAb[n + j] - autoAb[n + j];
            var mag = Math.sqrt(da * da + db * db);
            var w = (mag - t0) / (t1 - t0); w = w < 0 ? 0 : (w > 1 ? 1 : w); w = w * w * (3 - 2 * w);
            out[j] = baseAb[j] * (1 - w) + hintedAb[j] * w;
            out[n + j] = baseAb[n + j] * (1 - w) + hintedAb[n + j] * w;
        }
        return out;
    }

    // ── Collapse rescue (DDColor-tiny) ───────────────────────────────────
    // The tiny checkpoint honestly returns a near-zero ab plane for a whole
    // class of real photos (hard contrast / deep shadows / blown highlights —
    // ~38% of real runs measured 07..08-2026, verified against the OFFICIAL
    // torch pipeline: same collapse, so it's the model, not our export).
    // Compressing the input into a narrow mid band revives it (powerhouse
    // 0.1→16 measured); the composite keeps the ORIGINAL luminance, so only
    // the colour plane comes from the compressed pass.
    function planeChroma(ab) {
        var n = DD_SIZE * DD_SIZE, s = 0;
        for (var j = 0; j < n; j++) { var a = ab[j], b = ab[n + j]; s += Math.sqrt(a * a + b * b); }
        return Math.round(s / n * 10) / 10;
    }
    function rescueCompress(srcCanvas, lo, hi) {
        lo = lo == null ? 110 : lo; hi = hi == null ? 150 : hi;
        var c = document.createElement('canvas'); c.width = srcCanvas.width; c.height = srcCanvas.height;
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.drawImage(srcCanvas, 0, 0);
        var img = cx.getImageData(0, 0, c.width, c.height), d = img.data, k = (hi - lo) / 255;
        for (var i = 0; i < d.length; i += 4) { d[i] = lo + d[i] * k; d[i + 1] = lo + d[i + 1] * k; d[i + 2] = lo + d[i + 2] * k; }
        cx.putImageData(img, 0, 0);
        return c;
    }
    // Identity ab of the source itself (256²) — the honest give-up: the output
    // keeps the input's own colours (sepia stays sepia) instead of stripping
    // them to neutral gray. The output must never be LESS colourful than the
    // input ("стала ещё более чёрно-белой", report #1248).
    function sourceAb(srcCanvas) {
        var s = DD_SIZE, n = s * s, c = document.createElement('canvas'); c.width = s; c.height = s;
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.drawImage(srcCanvas, 0, 0, s, s);
        var d = cx.getImageData(0, 0, s, s).data, ab = new Float32Array(2 * n);
        for (var i = 0, j = 0; j < n; i += 4, j++) { var lab = rgbToLabAb(d[i], d[i + 1], d[i + 2]); ab[j] = lab[0]; ab[n + j] = lab[1]; }
        return ab;
    }

    // ── Is the source ALREADY in colour? ─────────────────────────────────
    // A colorizer invents colour from LUMINANCE only, so an input that already
    // has colour loses it: the model's guess replaces the real chroma. The
    // tools used to do that silently. Report 27.09.2026 (/ru/video-colorize,
    // «награждение.mp4», 1 star «Не работает»): the preview put the person's own
    // colours next to a warm, sepia-ish re-guess, and nothing on the page said
    // why. Over 20 days most uploads there are modern footage by file name
    // (phone clips, screen recordings, weddings, «исправить цвет.mp4»).
    //
    // What separates colour from monochrome — measured, not guessed
    // (infra/colorize-colour/, 50 real colour frames, 109 archival B&W frames
    // from 11 public-domain films, 226 hard synthetic cases):
    //  • a TINT is a colour that depends only on luminance (sepia, split
    //    toning, cyanotype, a bluish TV capture) → model it as a median a*b*
    //    per luminance band and keep only what the tint cannot explain;
    //  • what remains in B&W footage is small overlays — a channel logo, yellow
    //    subtitles, compression chroma noise — and it sits near ONE hue;
    //  • real colour footage has colour AWAY from its dominant hue.
    // Feature: share of the frame whose colour is ≥ COLOUR_RESID from the tint
    // AND > COLOUR_OFF_DEG from the dominant hue, on a smoothed 64-px frame
    // (noise averages out, real colour areas do not). All B&W frames, including
    // worst cases (big two-line yellow subtitles with a black outline, a colour
    // logo, blown sepia, a VHS-noise capture, a 1945 newsreel captured off TV
    // with a blue channel logo): max 1.31 %. Real colour: street 6.9–8 %, dash
    // cam 5.9–7.6 %, park 12–15 %, person in a room median 2.9 %. Threshold
    // 2 % per frame, majority of the sampled frames per video.
    // Deliberately NOT caught: faded colour (≤ 35 % saturation) and one-hue
    // scenes (yellow-green meadow) — those behave exactly as before, and a
    // faded film is a fair thing to re-colorize. A false «already colour» on a
    // B&W film would be the expensive mistake, so the margin sits on that side.
    var COLOUR_SIDE = 64, COLOUR_BANDS = 32, COLOUR_RESID = 10, COLOUR_OFF_DEG = 45, COLOUR_OFF_SHARE = 0.02;
    function _labOf(r, g, b) {
        var lr = srgbToLinear(r), lg = srgbToLinear(g), lb = srgbToLinear(b);
        var x = (lr * 0.4124 + lg * 0.3576 + lb * 0.1805) / 0.95047;
        var y = lr * 0.2126 + lg * 0.7152 + lb * 0.0722;
        var z = (lr * 0.0193 + lg * 0.1192 + lb * 0.9505) / 1.08883;
        function f(t) { return t > 0.008856 ? Math.cbrt(t) : (7.787 * t + 16 / 116); }
        var fx = f(x), fy = f(y), fz = f(z);
        return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
    }
    function _median(arr) {
        arr.sort(function (p, q) { return p - q; });
        var m = arr.length >> 1;
        return arr.length % 2 ? arr[m] : (arr[m - 1] + arr[m]) / 2;
    }
    // Pure core (RGBA bytes of an already-smoothed small frame) — testable in node.
    function frameColourRGBA(d, w, h) {
        var n = w * h, i, j, q;
        if (!n) return null;
        var L = new Float32Array(n), A = new Float32Array(n), B = new Float32Array(n);
        var bandA = [], bandB = [];
        for (q = 0; q < COLOUR_BANDS; q++) { bandA.push([]); bandB.push([]); }
        for (i = 0, j = 0; j < n; i += 4, j++) {
            var lab = _labOf(d[i], d[i + 1], d[i + 2]);
            L[j] = lab[0]; A[j] = lab[1]; B[j] = lab[2];
            q = Math.max(0, Math.min(COLOUR_BANDS - 1, Math.floor(lab[0] / 100 * COLOUR_BANDS)));
            bandA[q].push(lab[1]); bandB[q].push(lab[2]);
        }
        // Tint model: median a*b* per luminance band (≥ 4 px), linear between
        // band centres, clamped at the ends.
        var cL = [], cA = [], cB = [];
        for (q = 0; q < COLOUR_BANDS; q++) {
            if (bandA[q].length < 4) continue;
            cL.push((q + 0.5) * 100 / COLOUR_BANDS); cA.push(_median(bandA[q])); cB.push(_median(bandB[q]));
        }
        function tintAt(l, arr) {
            var m = cL.length;
            if (!m) return 0;
            if (l <= cL[0]) return arr[0];
            if (l >= cL[m - 1]) return arr[m - 1];
            for (var t = 1; t < m; t++) {
                if (l <= cL[t]) { var u = (l - cL[t - 1]) / (cL[t] - cL[t - 1]); return arr[t - 1] + (arr[t] - arr[t - 1]) * u; }
            }
            return arr[m - 1];
        }
        // Colours the tint cannot explain, and their hue.
        var hueOf = new Float32Array(n), own = new Uint8Array(n), nOwn = 0, hist = new Float64Array(36);
        for (j = 0; j < n; j++) {
            var ra = A[j] - tintAt(L[j], cA), rb = B[j] - tintAt(L[j], cB);
            if (Math.sqrt(ra * ra + rb * rb) <= COLOUR_RESID) continue;
            var hue = (Math.atan2(B[j], A[j]) * 180 / Math.PI + 360) % 360;
            hueOf[j] = hue; own[j] = 1; nOwn++;
            hist[Math.floor(hue / 10) % 36]++;
        }
        var off = 0;
        if (nOwn) {
            // Dominant hue: peak of a 10° histogram smoothed over ±1 bin.
            var best = -1, peak = 0;
            for (q = 0; q < 36; q++) {
                var s = hist[(q + 35) % 36] + hist[q] + hist[(q + 1) % 36];
                if (s > best) { best = s; peak = q * 10 + 5; }
            }
            for (j = 0; j < n; j++) {
                if (!own[j]) continue;
                var dd = Math.abs(((hueOf[j] - peak + 540) % 360) - 180);
                if (dd > COLOUR_OFF_DEG) off++;
            }
        }
        return { off: off / n, own: nOwn / n };
    }
    // Canvas / bitmap → the same, on a 64-px copy made by AVERAGING whole
    // blocks of pixels. Not drawImage-to-64: each browser smooths a 7× reduction
    // differently (Chrome's left thin subtitle strokes coloured enough to read
    // 2.6 % on a B&W frame the reference measured at 1.3 %), and the verdict must
    // not depend on the browser.
    function frameColour(src) {
        var w = src && src.width, h = src && src.height;
        if (!w || !h) return null;
        var k = COLOUR_SIDE / Math.max(w, h), cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
        var sw = Math.min(w, cw * 8), sh = Math.min(h, ch * 8);   // ≤ 8×8 source px per output px is plenty
        var c = document.createElement('canvas'); c.width = sw; c.height = sh;
        var cx = c.getContext('2d', { willReadFrequently: true });
        if (!cx) return null;
        cx.drawImage(src, 0, 0, sw, sh);
        var img = cx.getImageData(0, 0, sw, sh);
        if (!img) return null;
        var d = img.data, sum = new Float64Array(cw * ch * 3), cnt = new Float64Array(cw * ch);
        for (var y = 0; y < sh; y++) {
            var oy = Math.min(ch - 1, Math.floor(y * ch / sh));
            for (var x = 0; x < sw; x++) {
                var o = oy * cw + Math.min(cw - 1, Math.floor(x * cw / sw)), p = (y * sw + x) * 4;
                sum[o * 3] += d[p]; sum[o * 3 + 1] += d[p + 1]; sum[o * 3 + 2] += d[p + 2]; cnt[o]++;
            }
        }
        var out = new Uint8ClampedArray(cw * ch * 4);
        for (var q = 0; q < cw * ch; q++) {
            var m = cnt[q] || 1;
            out[q * 4] = sum[q * 3] / m; out[q * 4 + 1] = sum[q * 3 + 1] / m; out[q * 4 + 2] = sum[q * 3 + 2] / m; out[q * 4 + 3] = 255;
        }
        return frameColourRGBA(out, cw, ch);
    }
    // Frames of ONE video/photo → verdict. Colour only when most of the sampled
    // frames say so (a colour intro or a lit title card must not flip a B&W
    // film). `frames` = array of frameColour() results (nulls are skipped).
    function colourVerdict(frames, minFrames) {
        var list = [], i, k = 0, offs = [];
        for (i = 0; i < (frames || []).length; i++) if (frames[i]) list.push(frames[i]);
        for (i = 0; i < list.length; i++) { offs.push(list[i].off); if (list[i].off >= COLOUR_OFF_SHARE) k++; }
        var need = minFrames == null ? 3 : minFrames;
        var medOff = offs.length ? _median(offs.slice()) : 0;
        // decided:false = too few frames decoded to say anything (a one-keyframe
        // screen recording under a time budget) — not the same as «monochrome».
        var decided = list.length >= need;
        return { colour: decided && k * 2 > list.length, decided: decided, frames: list.length, colourFrames: k, offMed: Math.round(medOff * 1000) / 1000 };
    }

    root.ColorizeEngine = {
        DD_SIZE: DD_SIZE, MANGA_SIZE: MANGA_SIZE,
        grayInput: _grayInput,
        frameColourRGBA: frameColourRGBA, frameColour: frameColour, colourVerdict: colourVerdict,
        COLOUR_OFF_SHARE: COLOUR_OFF_SHARE,
        configure: configure, onLegacyOrt: function () { return !!_ort.legacy; }, loadOrt: loadOrt, fetchModel: fetchModel, isDownloadError: isDownloadError, createSession: createSession, hasWebGPU: hasWebGPU,
        inferDDColor: inferDDColor, inferManga: inferManga, mockAb: mockAb, applyVibrance: applyVibrance,
        hasWorker: hasWorker, createWorkerSession: createWorkerSession, inferDDColorWorker: inferDDColorWorker, inferMangaWorker: inferMangaWorker, resetWorker: resetWorker,
        inferHints: inferHints, inferHintsWorker: inferHintsWorker, regionBlend: regionBlend,
        planeChroma: planeChroma, rescueCompress: rescueCompress, sourceAb: sourceAb,
        compositeFromAb: compositeFromAb, compositeLab: compositeLab, upsampleAb: upsampleAb,
        applyLook: applyLook, applyHueRemap: applyHueRemap, computeNormMetric: computeNormMetric,
        srgbToLinear: srgbToLinear, linearToSrgb: linearToSrgb, rgbToLabAb: rgbToLabAb
    };
})(typeof window !== 'undefined' ? window : globalThis);
